import type { ToastVariant } from '@/components/ui/UnifiedToast';

/**
 * Whether a toast leaves a record in the notification bell.
 *
 * THE RULE (owner 2026-09-17, approved): RETAIN BY DEFAULT, OPT OUT EXPLICITLY.
 *
 * Retention used to be a privilege a call site had to request, via
 * `scope: 'global'` on the one entry point that consulted a scope at all — and
 * the default, `page`, resolved through `shouldStore()` to "persist nothing".
 * Nothing requested it: `scope: 'global'` had zero callers, `scope: 'session'`
 * had zero, and the only writer to the notifications table anywhere in the
 * monorepo was the achievements self-heal route. A whole subsystem — the DB
 * route, the session store, the deduplicator, the rate limiter, the
 * per-category expiry, the informational/actionable split — was built and then
 * switched off by one default. Default-on with VISIBLE exceptions cannot fail
 * that way: every exclusion is a `retain: false` at a call site you can grep
 * for, rather than an absence you have to notice.
 *
 * WHAT IS EXCLUDED — five classes, each leaving no durable fact behind, each
 * opting out at its own call site:
 *   1. Clipboard and pure UI acknowledgements — the remedy is on screen.
 *   2. Session/auth acknowledgements ("Logged in successfully") — the session
 *      is its own record.
 *   3. Courtesy acknowledgements ("Thank you for your feedback!") — the
 *      outcome ("Feedback submitted") is retained in its place.
 *   4. The `loading` variant (below).
 *   5. Recovery pairs collapse to their outcome: the warning is retained, and
 *      the success only as that warning's resolution. `aiCallerStatusToast.ts`
 *      already gates those successes on the warning having fired.
 *
 * A FAILURE CANNOT OPT OUT. An earlier draft said "errors are retained" and
 * then excluded "Failed to copy to clipboard. Please try again." — an error.
 * An unstated exception inside a stated rule is precisely how the situation
 * above arose, so the exception was removed rather than documented, and the
 * invariant is enforced here instead of trusted to call sites. It costs one
 * bell entry and removes a carve-out a reader would otherwise discover by
 * reading code.
 *
 * ACCEPTED LIMITATION — an operation that dies without resolving its spinner.
 * `loading` is excluded as superseded by its own resolution, and the question
 * is fair: what if the resolution never comes? In this codebase it does not
 * silently vanish — `show-unified-toast.ts` gives `loading` duration
 * `Infinity`, so an unresolved spinner PINS ITSELF ON SCREEN. That is a stuck
 * indicator, not a lost record, and it belongs to the owner's separate standing
 * rule about never waiting forever. What IS lost is the case where the page is
 * navigated or reloaded while such a toast is open: that leaves nothing behind,
 * and this rule does not fix it. Retaining a resolution-timeout would, and is
 * deliberately not built here.
 */
export interface ToastRetentionInput {
  variant: ToastVariant;
  title: string;
  /**
   * Set `false` ONLY for one of the five excluded acknowledgement classes. It
   * is ignored for `error` and `warning` — a call site cannot hide a failure
   * from the bell.
   */
  retain?: boolean;
  /**
   * Another path already owns a record for this notification, so the toast
   * layer must not make a SECOND one. This is NOT the opt-out: it never takes
   * the record count to zero, so unlike `retain` it is honoured for failures
   * too. `useUnifiedNotifications.notify.error` sets it because it writes the
   * durable row itself, scope-independently ("errors always persist to DB").
   */
  alreadyRecorded?: boolean;
}

/** Variants that always leave a record, whatever the call site asks for. */
const FAILURE_VARIANTS: ReadonlySet<ToastVariant> = new Set(['error', 'warning']);

export function shouldRetainToast(input: ToastRetentionInput): boolean {
  // Checked before the failure rule on purpose: a failure that is already
  // recorded elsewhere must not be recorded twice. One record, not none.
  if (input.alreadyRecorded === true) {
    return false;
  }
  if (FAILURE_VARIANTS.has(input.variant)) {
    return true;
  }
  if (input.variant === 'loading') {
    return false;
  }
  return input.retain !== false;
}
